// کلیدهای Query و کمک‌های داده Console (AP-PLT-02). همه فراخوان‌ها با کلاینت تایپ‌شده PlatformGate (توکن Keycloak).
import type {
  ConsoleTenantDetail,
  PlanView,
  ReferenceDatasetView,
  TenantAccessMode,
  TenantStatus,
} from '@phoenix/api-client';
import type { StatusTone } from '@phoenix/ui';

export const consoleKeys = {
  tenants: (filters: Record<string, unknown>) => ['platform', 'console', 'tenants', filters] as const,
  tenantsRoot: ['platform', 'console', 'tenants'] as const,
  tenant: (id: string) => ['platform', 'console', 'tenant', id] as const,
  overrides: (id: string) => ['platform', 'console', 'tenant', id, 'overrides'] as const,
  plans: ['platform', 'console', 'plans'] as const,
  referenceData: ['platform', 'console', 'reference-data'] as const,
  // AP-PLT-08: لایسنس On-prem (SCR-CONSOLE-005).
  licenses: (customerId: string) => ['platform', 'console', 'licenses', customerId] as const,
  licensesRoot: ['platform', 'console', 'licenses'] as const,
  licenseSigningKey: ['platform', 'console', 'licenses', 'signing-key'] as const,
  audit: (filters: Record<string, unknown>) => ['platform', 'console', 'audit', filters] as const,
  // AP-PLT-05.
  smsCredit: (id: string) => ['platform', 'console', 'tenant', id, 'sms-credit'] as const,
  supportAccess: (tenantId: string | null) => ['platform', 'console', 'support-access', tenantId] as const,
  supportAccessRoot: ['platform', 'console', 'support-access'] as const,
  announcements: ['platform', 'console', 'announcements'] as const,
  tickets: ['platform', 'console', 'support-tickets'] as const,
  ticket: (id: string) => ['platform', 'console', 'support-tickets', id] as const,
  // AP-CFG-01.
  packs: ['configuration', 'console', 'specialty-packs'] as const,
  packVersion: (id: string) => ['configuration', 'console', 'specialty-pack-version', id] as const,
};

/** int64/int32 سرور ممکن است رشته باشد (OpenAPI)؛ مقادیر شمارشی کوچک‌اند و به number تبدیل می‌شوند. */
export function toNumber(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** رنگ ثابت وضعیت مستأجر (Design System §2.1). */
export function tenantStatusTone(status: TenantStatus): StatusTone {
  switch (status) {
    case 'Active':
      return 'active';
    case 'Provisioning':
    case 'PendingTermination':
      return 'pending';
    case 'ProvisioningFailed':
      return 'error';
    case 'Suspended':
      return 'no_show';
    default:
      return 'inactive';
  }
}

export function accessModeTone(mode: TenantAccessMode): StatusTone {
  switch (mode) {
    case 'Full':
      return 'active';
    case 'ReadOnly':
      return 'pending';
    case 'Locked':
    case 'Suspended':
      return 'no_show';
    default:
      return 'inactive';
  }
}

/** وضعیت در حال گذار Provisioning (Polling تا Active یا ProvisioningFailed). */
export function isProvisioning(tenant: Pick<ConsoleTenantDetail, 'status'> | undefined): boolean {
  return tenant?.status === 'Provisioning';
}

export const PROVISIONING_POLL_MS = 2_000;

/** نسخه‌های منتشرشده پلن (قابل انتخاب برای اشتراک تازه — C-AGENT-159). */
export function publishedVersions(plan: PlanView) {
  return plan.versions.filter((version) => version.status === 'Published');
}

/** آخرین نسخه پلن (بیشترین شماره). */
export function latestVersion(plan: PlanView) {
  return [...plan.versions].sort((a, b) => (toNumber(b.version) ?? 0) - (toNumber(a.version) ?? 0))[0];
}

/** نسخه مؤثر هر مجموعه‌داده (آخرین منتشرشده). */
export function latestPublished(dataset: ReferenceDatasetView) {
  return dataset.versions
    .filter((version) => version.status === 'Published')
    .sort((a, b) => (toNumber(b.version) ?? 0) - (toNumber(a.version) ?? 0))[0];
}
